import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_LANGUAGE } from '@/constants/languages';
import { getLanguageFromPath } from '@/utils/utils';
import { useSearchParams } from 'react-router-dom';
import logger from '@/logger';
import { Roles } from '@/pages/ClientProfiles/Roles.types';
import { useSupabaseStore } from './supabaseStore';
import { useRoleStore } from './useRole';

export interface SessionData {
	access_token: string;
	expires_at?: number;
	user: {
		id: string;
		email?: string;
	};
}

export function useAuth() {
	const [session, setSession] = useState<SessionData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const supabase = useSupabaseStore((state) => state.supabase);
	const roleStore = useRoleStore();

	const navigateWithLang = useCallback(
		(path: string) => {
			const currentLang = getLanguageFromPath(window.location.pathname) || DEFAULT_LANGUAGE;
			navigate(`/${currentLang}${path}`);
		},
		[navigate]
	);

	const isSessionExpired = useCallback((session: SessionData | null) => {
		if (!session?.expires_at) return true;
		const expiryTime = new Date(session.expires_at * 1000);
		return new Date() > expiryTime;
	}, []);

	const signIn = async (email: string, password: string, searchParams: URLSearchParams) => {
		try {
			setError(null);
			setLoading(true);

			const { data, error: authError } = await supabase.auth.signInWithPassword({
				email: email.trim(),
				password
			});

			if (authError) throw authError;

			if (data?.session) {
				const { data: roleData, error: roleError } = await supabase
					.from('user_roles')
					.select('role')
					.eq('user_id', data.session.user.id)
					.single();

				if (roleError) throw roleError;

				const validRoles = [Roles.CUSTOMER_SUPPORT, Roles.KOGNI_ADMIN];
				if (!roleData || validRoles.indexOf(roleData.role) === -1) {
					await supabase.auth.signOut();
					setError('unauthorizedAccess');
					throw new Error('unauthorized');
				}

				roleStore.saveRole(roleData.role);
				setSession(data.session);

				const returnTo = searchParams.get('returnTo');
				if (returnTo) {
					const decodedPath = decodeURIComponent(returnTo);
					window.location.href = decodedPath;
					return;
				}

				navigateWithLang(`/center/customer-support`);
			}
		} catch (err) {
			logger.error('Sign in error:', err);
			setError(
				err instanceof Error && err.message === 'unauthorized'
					? 'unauthorizedAccess'
					: 'invalidCredentials'
			);
			throw err;
		} finally {
			setLoading(false);
		}
	};

	const signOut = async () => {
		try {
			setLoading(true);
			await supabase.auth.signOut();
			setSession(null);
			navigateWithLang('/center/support');
		} catch (error) {
			logger.error('Sign out error:', error);
			navigateWithLang('/center/support');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		let mounted = true;
		let hasRedirected = false;
		const returnTo = searchParams.get('returnTo');

		const checkSession = async () => {
			try {
				console.log('Checking session...');
				const {
					data: { session },
					error: sessionError
				} = await supabase.auth.getSession();
				console.log('Session check result:', { session, error: sessionError });

				if (sessionError) throw sessionError;

				if (session && !isSessionExpired(session)) {
					if (mounted && !hasRedirected) {
						setSession(session);
						hasRedirected = true;
						if (returnTo) {
							const decodedPath = decodeURIComponent(returnTo);
							window.location.href = decodedPath;
						} else {
							navigateWithLang(`/center/customer-support`);
						}
					} else {
						setSession(session);
					}
				}
			} catch (err) {
				logger.error('Session check error:', err);
				if (mounted) {
					setSession(null);
					setError('sessionError');
				}
			} finally {
				if (mounted) {
					console.log('Setting loading to false');
					setLoading(false);
				}
			}
		};

		checkSession();

		const {
			data: { subscription }
		} = supabase.auth.onAuthStateChange(async (event, session) => {
			console.log('Auth state change:', { event, hasSession: !!session });
			if (!mounted) return;

			if (event === 'SIGNED_IN' && session) {
				setSession(session);
				if (!hasRedirected) {
					hasRedirected = true;
					if (returnTo) {
						const decodedPath = decodeURIComponent(returnTo);
						window.location.href = decodedPath;
					} else {
						navigateWithLang(`/center/customer-support`);
					}
				}
			} else if (event === 'SIGNED_OUT') {
				setSession(null);
				hasRedirected = false;
				if (window.location.pathname.includes('/customer-support')) {
					navigateWithLang('/center/support');
				}
			}
		});

		return () => {
			mounted = false;
			subscription?.unsubscribe();
		};
	}, [supabase, navigateWithLang, isSessionExpired, searchParams]);

	return {
		session,
		loading,
		error,
		signIn,
		signOut,
		isSessionExpired
	};
}
